import { useEffect, useState } from "react";

import * as _ from "lodash";

import { Button, ShareBar } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import {
    ProgressBarContainer,
    ProgressBarLabel,
    ShareBarWrapperForHomePage,
} from "app/components/Accordion/AccordionHeadStyled";
import { FeedbackSideTab } from "app/components/React/sideTabs";
import { RewardsSideBarContainer } from "app/components/React/sideTabs/RewardsSideBarContainer";
import { i18nFilter } from "app/filters/i18nFilter";
import { AutocompleteDigitalMarketing } from "pro-features/components/AutocompleteDigitalMarketing/AutocompleteDigitalMarketing";

import { HomePageBanner } from "app/pages/digital-suite/home-page/components/HomePageTrialNTLayout/HomePageBanner";
import { ActivationWizard } from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizard";
import {
    EnumWizardItems,
    getWithInitActivationWizardItems,
    setActivationWizardItems,
    getShowGetStarted,
    setShowGetStarted,
} from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardApi";
import {
    EStepState,
    WizardRole,
} from "app/pages/digital-suite/home-page/components/ActivationWizard/ActivationWizardUtils";
import { useActivationWizardSteps } from "app/pages/digital-suite/home-page/components/ActivationWizard/Components/UseActivationWizardSteps";
import { CustomAssetsWidget } from "app/pages/digital-suite/home-page/components/CustomAssets/CustomAssetsWidget";

import { getAssetCreationItems } from "./components/CustomAssets/AssetCreationButton/AssetCreationButtonOptions";
import { AssetName } from "./components/CustomAssets/types";

import { FeedbackBanner } from "app/pages/digital-suite/home-page/components/FeedbackBanner/FeedbackBanner";
import { useUserInvitation } from "app/pages/digital-suite/home-page/components/InviteUsers/UseUserInvitation";
import {
    AutocompleteWrapper,
    ContainerWrapper,
    Header,
    HeaderTitle,
    ImgStyle,
    PageContainer,
    ProgressBarActivationWizard,
    Title,
    WidgetWrapperWithTransition,
} from "app/pages/digital-suite/home-page/components/HomePageTrialNTLayout/DigitalSuiteTrialHomePageStyled";
import { PopularActions } from "app/pages/digital-suite/home-page/components/PopularActions/popularActions";
import { RecentService } from "app/services/recent/recentService";
import { KeywordsWebsitesRecentsWidget } from "app/pages/digital-suite/home-page/components/RecentsWidget/KeywordsWebsitesRecentsWidget";
import { KeywordsGroupEditorModal } from "app/pages/keyword-analysis/KeywordsGroupEditorModal";
import { AssetsService } from "app/services/AssetsService";
import { allTrackers } from "app/services/track/track";
import { AssetCreationButton } from "./components/CustomAssets/AssetCreationButton/AssetCreationButton";
import { navigateToKeywordList } from "./components/CustomAssets/customAssetsUtils";

const WIZARD_STEP_TRACKING_EVENT_NAMES: Record<string, string> = {
    "growth.homepage.wizard.button.skip": "Activation widget/Skip step/%step%",
    "growth.homepage.wizard.continue.open": "click to continue activation widget",
    "growth.homepage.wizard.button.dashboard": "Activation widget/Step Done/%step% - Dashboard",
    "growth.homepage.wizard.button.dashboardMetric": "Activation widget/Step Done/%step% - Custome Dashboard",
    "growth.homepage.wizard.button.createKeywordList": "Activation widget/SEO/Step Done/%step% - Keyword Generator",
    "growth.homepage.wizard.button.trafficAndEngagementSuggested":
        "Activation widget/Step Done/%step% - Traffic and Engagement Suggested",
    "growth.homepage.wizard.button.trafficAndEngagementTryOwn":
        "Activation widget/Step Done/%step% - Traffic and Engagement Try Own",
    "growth.homepage.wizard.button.keywords": "Activation widget/Step Done/%step% - Keywords",
    "growth.homepage.wizard.button.generateTopicKeywords":
        "Activation widget/SEO/Step Done/%step% - Keyword Generator Try Own",
    "growth.homepage.wizard.button.marketingChannels": "Activation widget/Step Done/%step% - Marketing Channels",
    "growth.homepage.wizard.button.monitorKeywordList": "Activation widget/SEO/Step Done/%step% - Monitor Keyword List",
    "growth.homepage.wizard.button.analyzeKeywordList": "Activation widget/SEO/Step Done/%step% - Keyword Overview",
};

export const DigitalSuiteNewHomePage = ({ hasAvailableAllocations = false }) => {
    const i18n = i18nFilter();
    const {
        user: {
            customData: { quiz },
        },
    } = swSettings;
    const userFirstName = _.capitalize(swSettings.user.firstname);
    const wizardRole = WizardRole[quiz?.DepartmentType] || WizardRole.General;
    const [isActivationWizardOpen, setIsActivationWizardOpen] = useState<boolean>(true);
    const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = useState<boolean>();
    const { invitationButtonComponent, invitationBannerComponent, invitationModalComponent, isProminent } =
        useUserInvitation({ hasAvailableAllocations });

    const { steps, stepsState, setStepState, getCompletionStep, resetSteps } = useActivationWizardSteps(wizardRole);

    useEffect(() => {
        if (!stepsState) {
            return;
        }
        let tempStates = [...stepsState];

        //the server isn't familiar with this user's steps - create new ones
        if (!tempStates || (Object.keys(tempStates) ?? []).length !== steps.length) {
            tempStates = steps.map((_, index) => ({
                index: index,
                state: stepsState?.[index]?.state ?? EStepState.INCOMPLETE,
            }));

            //initialize steps states for this user
            tempStates.forEach((state) => setStepState(state));
        }
    }, [stepsState]);

    useEffect(() => {
        const getIsActivationWizardOpen = getWithInitActivationWizardItems(EnumWizardItems.IsActivationWizardOpen);
        if (getIsActivationWizardOpen != null) {
            setIsActivationWizardOpen(getIsActivationWizardOpen);
        } else {
            setIsActivationWizardOpen(true);
            setActivationWizardItems(EnumWizardItems.IsActivationWizardOpen, true);
        }
    }, []);

    const trackWithGoal = (guid: string, step?: string) => {
        const eventNameTemplate = WIZARD_STEP_TRACKING_EVENT_NAMES[guid];
        if (!eventNameTemplate) {
            return;
        }
        allTrackers.dispatchTrackEvent("Button", "click", eventNameTemplate.replace(/%step%/g, step ?? ""));
    };

    const countStepsByState = (state: EStepState) =>
        stepsState?.filter((stepState) => stepState.state === state).length ?? 0;

    const onClose = () => {
        setIsActivationWizardOpen(false);
        setActivationWizardItems(EnumWizardItems.IsActivationWizardOpen, false);
        setActivationWizardItems(EnumWizardItems.IsAccordionOpen, true);
        if (getIsStepsCompleted()) {
            setShowGetStarted(true);
            resetSteps();
        }
    };

    const onSkip = (stepIndex: number): void => {
        const currentState = stepsState.find((s) => s.index === stepIndex)?.state;
        const isComplete = currentState === EStepState.COMPLETED;
        const nextState = isComplete ? EStepState.COMPLETED : EStepState.SKIP;

        setStepState({
            index: stepIndex,
            state: nextState,
        });

        trackWithGoal("growth.homepage.wizard.button.skip", `step ${stepIndex + 1}`);
    };

    const onComplete = (index: number, guidTracking: string): void => {
        setStepState(
            {
                index: index,
                state: EStepState.COMPLETED,
            },
            true,
        );

        trackWithGoal(guidTracking, `step ${index + 1}`);
    };

    const resetSkippedToIncomplete = (): void => {
        stepsState
            .filter((stateData) => stateData.state === EStepState.SKIP)
            .forEach((stateData) => {
                setStepState({
                    index: stateData.index,
                    state: EStepState.INCOMPLETE,
                });
            });
    };

    const getContinueActivationWizard = () => (
        <ProgressBarActivationWizard
            data-automation={"activation-wizard-progress-bar"}
            isOpen={!isActivationWizardOpen}
        >
            <ProgressBarContainer>
                <ProgressBarLabel>
                    {i18n("growth.homepage.layout.header.activationWizard.progressBarInTitle", {
                        countCompletedSteps: countStepsByState(EStepState.COMPLETED),
                        countAllSteps: stepsState?.length ?? 0,
                    })}
                </ProgressBarLabel>
                <ShareBarWrapperForHomePage>
                    <ShareBar
                        value={stepsState?.length ? countStepsByState(EStepState.COMPLETED) / stepsState.length : 0}
                        hideChangeValue
                        hideValue
                    />
                </ShareBarWrapperForHomePage>
            </ProgressBarContainer>
            <Button
                height="30px"
                type="flat"
                onClick={() => {
                    trackWithGoal("growth.homepage.wizard.continue.open");
                    setIsActivationWizardOpen(true);
                    setActivationWizardItems(EnumWizardItems.IsActivationWizardOpen, true);
                }}
            >
                {i18n("growth.homepage.layout.header.activationWizard.continueButton")}
            </Button>
        </ProgressBarActivationWizard>
    );

    const getIsStepsCompleted = () => countStepsByState(EStepState.COMPLETED) === stepsState.length;
    const onModalClick = () => {
        const isQueryLimitReached = RecentService.isQueryLimiterEnabled();
        isQueryLimitReached ? RecentService.getQueryLimiterHook() : setIsModalOpen(true);
    };

    return (
        <PageContainer
            className="main-homepage-container"
            data-automation="platform-homepage"
        >
            <FeedbackSideTab />
            <RewardsSideBarContainer />
            <ContainerWrapper>
                <Header>
                    <HeaderTitle>
                        <Title data-automation={"header-welcome"}>
                            {i18n("growth.homepage.layout.header.title", {
                                name: userFirstName,
                            })}
                            &nbsp;
                            <ImgStyle
                                src={AssetsService.assetUrl("/images/homepage/nav/hand-icon-new-home-page.svg")}
                                alt=""
                            />
                        </Title>
                        <HomePageBanner
                            inviteUsersButton={invitationButtonComponent}
                            isProminent={isProminent}
                        />
                    </HeaderTitle>
                    {stepsState && !getIsStepsCompleted() && !getShowGetStarted()
                        ? getContinueActivationWizard()
                        : null}
                </Header>
                <AutocompleteWrapper data-automation="search-bar">
                    <AutocompleteDigitalMarketing
                        includeKeywordsData
                        maxCompareItemsToDisplay={1}
                        onAutocompleteOpen={() => setAutocompleteOpen(true)}
                        onAutocompleteClose={() => setAutocompleteOpen(false)}
                    />
                </AutocompleteWrapper>
                <WidgetWrapperWithTransition
                    className={`transition-${isActivationWizardOpen ? "open" : "closed"}`}
                    data-automation={"activation-wizard-wrapper"}
                    isWidgetOpen={isActivationWizardOpen}
                    componentHeight={700}
                >
                    <ActivationWizard
                        role={wizardRole}
                        steps={steps}
                        stepsState={stepsState}
                        onStepSkip={onSkip}
                        onStepComplete={onComplete}
                        resetSkippedToIncomplete={resetSkippedToIncomplete}
                        onClose={onClose}
                        countStepsByState={countStepsByState}
                        getCompletionStep={getCompletionStep}
                        trackWithGoal={trackWithGoal}
                    />
                </WidgetWrapperWithTransition>

                <WidgetWrapperWithTransition
                    className={`transition-${!isActivationWizardOpen ? "open" : "closed"}`}
                    data-automation={"popular-actions-wrapper"}
                    isWidgetOpen={!isActivationWizardOpen}
                    componentHeight={120}
                >
                    <PopularActions />
                </WidgetWrapperWithTransition>

                {invitationBannerComponent}

                <KeywordsWebsitesRecentsWidget />
                <CustomAssetsWidget
                    emptyStateTypes={[AssetName.Dashboard]}
                    title="digitalmarketing.homepage.custom_assets.title"
                    ctaButton={<AssetCreationButton items={getAssetCreationItems(() => onModalClick())} />}
                />
                <KeywordsGroupEditorModal
                    onClose={() => setIsModalOpen(false)}
                    open={isModalOpen}
                    onSave={({ Id: keywordListId }) => navigateToKeywordList(keywordListId)}
                    showGoToMonitorKeywordListButton
                />
                <FeedbackBanner />
                {invitationModalComponent}
            </ContainerWrapper>
        </PageContainer>
    );
};
